import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { TEXT_COLOR } from '../../main-app/theme';
import hornIcon from '../../main-app/images/icon-horn.svg';
import hornIconAvisSolution from '../../main-app/images/icon-horn--avis-solution.svg';
import shareIcon from '../../main-app/images/icon-share.svg';
import pencilIcon from '../../main-app/images/icon-pencil.svg';
import shareIconAvisSolution from '../../main-app/images/icon-share--avis-solution.svg';
import { AnswerButton } from './AnswerButton';
import { AnswerForm } from './AnswerForm';
import { InsatisfactionForm } from './InsatisfactionForm';
import { MesureCorrectiveForm } from './MesureCorrectiveForm';
import { ToolbarButton } from './ToolbarButton';

const TOOLBAR_BUTTON_MARGIN = 17;

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 13px;

  @media(max-width: 768px) {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
  }
`;

const LeftSide = styled.div`
  display: flex;
  align-items: center;
`;

const RightSide = styled.div`
  display: flex;
  align-items: center;
`;

const ToolbarButtonReport = styled(ToolbarButton)`
  padding-left: 23px;
  background: url(${({ isAvisSolution }) => (isAvisSolution ? hornIconAvisSolution : hornIcon)})
    no-repeat 0 50%;
  background-size: 20px 20px;
  height: 20px;
`;

const ToolbarButtonShare = styled(ToolbarButton)`
  padding-left: 23px;
  background: url(${({ isAvisSolution }) => (isAvisSolution ? shareIconAvisSolution : shareIcon)})
    no-repeat 0 50%;
  background-size: 20px 20px;
  height: 20px;
  margin-left: ${TOOLBAR_BUTTON_MARGIN}px;
`;

const AnswerBoxContainer = styled.div`
  background-color: #eef1f6;
  padding: 12px 24px 16px;
  margin-left: 18px;
  border-radius: 8px;
  margin-bottom: 17px;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 15px;
  align-items: flex-start;
`;

const AnswerBoxArrowLeft = styled.svg`
  position: absolute;
  top: 10px;
  left: -18px;
`;

const AnswerTitleContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`;

const AnswerTitle = styled.div`
  font-size: 12px;
  line-height: 1.17;
  letter-spacing: 0.1px;
  color: ${TEXT_COLOR};
  font-weight: bold;
`;

const ModifyButton = styled(ToolbarButton)`
  padding-left: 23px;
  background: url(${pencilIcon}) no-repeat 0 50%;
  background-size: 18px 18px;
  height: 20px;
  margin-left: 12px;
  font-size: 11px;
`;

const AnswerContent = styled.div`
  font-size: 16px;
  line-height: 1.25;
  letter-spacing: 0.1px;
  color: #354354;
  white-space: pre-line;
`;

const ReviewToolbar = (props) => {
  const {
    answer,
    onAnswerClick,
    hasAnswerForm,
    hasAnswerButton,
    hasUpdateButton,
    onAnswerChange,
    onAnswerSubmit,
    isAvisSolution,
    onAnswerCancel,
    isAnswerSending,
    showReportWindow,
    showShareLinkWindow,
    isServiceButtonsSectionVisible,
    isAnswerError,
    isSchool,
    answerValue,
    isModifyView,
    hasInsatisfactionForm,
    insatisfactionValue,
    onInsatisfactionClick,
    onInsatisfactionChange,
    onInsatisfactionSubmit,
    onInsatisfactionCancel,
    isInsatisfactionSending,
    isInsatisfactionError,
    hasInsatisfactionButton,
    hasMesureCorrectiveForm,
    mesureCorrectiveValue,
    onMesureCorrectiveClick,
    onMesureCorrectiveChange,
    onMesureCorrectiveSubmit,
    onMesureCorrectiveCancel,
    isMesureCorrectiveSending,
    isMesureCorrectiveError,
    hasMesureCorrectiveButton,
  } = props;

  return (
    <>
      {(answer
        || hasAnswerForm
        || hasInsatisfactionForm
        || hasMesureCorrectiveForm
        || insatisfactionValue
        || mesureCorrectiveValue) && (
        <AnswerBoxContainer>
          <AnswerBoxArrowLeft
            width="19"
            height="25"
            viewBox="0 0 19 25"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill="#eef1f6"
              d="M0 .024c6.37.117 12.545 1.023 18.432 2.622.111-.395.111 7.056 0 22.354-1.148-11.2-8.636-20.374-18.432-24.976z"
            />
          </AnswerBoxArrowLeft>

          <>
            {hasAnswerForm ? (
              <AnswerForm
                value={answerValue}
                onChange={onAnswerChange}
                onSubmit={onAnswerSubmit}
                onCancel={onAnswerCancel}
                isSending={isAnswerSending}
                hasError={isAnswerError}
                isModifyView={isModifyView}
              />
            ) : (
              answer && (
                <div>
                  <AnswerTitleContainer>
                    <AnswerTitle>
                      {`Réponse ${isSchool ? "de l'auto-école" : "du centre d'examen"}`}
                    </AnswerTitle>
                    {isSchool && hasUpdateButton && (
                      <ModifyButton onClick={onAnswerClick}>Modifier</ModifyButton>
                    )}
                  </AnswerTitleContainer>
                  <AnswerContent>{answer}</AnswerContent>
                </div>
              )
            )}
          </>

          <>
            {hasInsatisfactionForm ? (
              <InsatisfactionForm
                value={insatisfactionValue}
                onChange={onInsatisfactionChange}
                onSubmit={onInsatisfactionSubmit}
                onCancel={onInsatisfactionCancel}
                isSending={isInsatisfactionSending}
                hasError={isInsatisfactionError}
              />
            ) : (
              insatisfactionValue && (
                <div>
                  <AnswerTitleContainer withTopMargin>
                    <AnswerTitle>Motif d&apos;insatisfaction</AnswerTitle>
                    {isSchool && (
                      <ModifyButton onClick={onInsatisfactionClick}>Modifier</ModifyButton>
                    )}
                  </AnswerTitleContainer>
                  <AnswerContent>{insatisfactionValue}</AnswerContent>
                </div>
              )
            )}
          </>

          <>
            {hasMesureCorrectiveForm ? (
              <MesureCorrectiveForm
                value={mesureCorrectiveValue}
                onChange={onMesureCorrectiveChange}
                onSubmit={onMesureCorrectiveSubmit}
                onCancel={onMesureCorrectiveCancel}
                isSending={isMesureCorrectiveSending}
                hasError={isMesureCorrectiveError}
              />
            ) : (
              mesureCorrectiveValue && (
                <div>
                  <AnswerTitleContainer withTopMargin>
                    <AnswerTitle>Mesure corrective</AnswerTitle>
                    {isSchool && (
                      <ModifyButton onClick={onMesureCorrectiveClick}>Modifier</ModifyButton>
                    )}
                  </AnswerTitleContainer>
                  <AnswerContent>{mesureCorrectiveValue}</AnswerContent>
                </div>
              )
            )}
          </>
        </AnswerBoxContainer>
      )}
      <Container>
        <LeftSide>
          {hasAnswerButton && <AnswerButton onAnswerClick={onAnswerClick} />}
          {hasInsatisfactionButton && (
            <ToolbarButton
              onClick={onInsatisfactionClick}
              style={{ marginLeft: TOOLBAR_BUTTON_MARGIN }}
            >
              Motif d&apos;insatisfaction
            </ToolbarButton>
          )}
          {hasMesureCorrectiveButton && (
            <ToolbarButton
              onClick={onMesureCorrectiveClick}
              style={{ marginLeft: TOOLBAR_BUTTON_MARGIN }}
            >
              Mesure corrective
            </ToolbarButton>
          )}
        </LeftSide>
        {isServiceButtonsSectionVisible && (
          <RightSide>
            <ToolbarButtonReport onClick={showReportWindow} isAvisSolution={isAvisSolution}>
              Signaler
            </ToolbarButtonReport>
            <ToolbarButtonShare onClick={showShareLinkWindow} isAvisSolution={isAvisSolution}>
              Partager
            </ToolbarButtonShare>
          </RightSide>
        )}
      </Container>
    </>
  );
};

ReviewToolbar.propTypes = {
  answer: PropTypes.string,
  answerValue: PropTypes.string,
  onAnswerClick: PropTypes.func,
  hasAnswerForm: PropTypes.bool,
  hasAnswerButton: PropTypes.bool,
  hasUpdateButton: PropTypes.bool,
  onAnswerChange: PropTypes.func,
  onAnswerSubmit: PropTypes.func,
  onAnswerCancel: PropTypes.func,
  isAnswerSending: PropTypes.bool,
  showReportWindow: PropTypes.func.isRequired,
  showShareLinkWindow: PropTypes.func.isRequired,
  isServiceButtonsSectionVisible: PropTypes.bool,
  isAnswerError: PropTypes.bool,
  isAvisSolution: PropTypes.bool,
  isSchool: PropTypes.bool,
  isModifyView: PropTypes.bool,
  hasInsatisfactionForm: PropTypes.bool,
  insatisfactionValue: PropTypes.string,
  onInsatisfactionClick: PropTypes.func,
  onInsatisfactionChange: PropTypes.func,
  onInsatisfactionSubmit: PropTypes.func,
  onInsatisfactionCancel: PropTypes.func,
  isInsatisfactionSending: PropTypes.bool,
  isInsatisfactionError: PropTypes.bool,
  hasInsatisfactionButton: PropTypes.bool,
  hasMesureCorrectiveForm: PropTypes.bool,
  mesureCorrectiveValue: PropTypes.string,
  onMesureCorrectiveClick: PropTypes.func,
  onMesureCorrectiveChange: PropTypes.func,
  onMesureCorrectiveSubmit: PropTypes.func,
  onMesureCorrectiveCancel: PropTypes.func,
  isMesureCorrectiveSending: PropTypes.bool,
  isMesureCorrectiveError: PropTypes.bool,
  hasMesureCorrectiveButton: PropTypes.bool,
};

ReviewToolbar.defaultProps = {
  answer: '',
  answerValue: '',
  onAnswerClick: () => {},
  hasAnswerForm: false,
  hasAnswerButton: false,
  hasUpdateButton: false,
  onAnswerChange: () => {},
  onAnswerSubmit: () => {},
  onAnswerCancel: () => {},
  isAnswerSending: false,
  isServiceButtonsSectionVisible: true,
  isAnswerError: false,
  isAvisSolution: false,
  isSchool: true,
  isModifyView: true,
  hasInsatisfactionForm: false,
  insatisfactionValue: '',
  onInsatisfactionClick: () => {},
  onInsatisfactionChange: () => {},
  onInsatisfactionSubmit: () => {},
  onInsatisfactionCancel: () => {},
  isInsatisfactionSending: false,
  isInsatisfactionError: false,
  hasInsatisfactionButton: false,
  hasMesureCorrectiveForm: false,
  mesureCorrectiveValue: '',
  onMesureCorrectiveClick: () => {},
  onMesureCorrectiveChange: () => {},
  onMesureCorrectiveSubmit: () => {},
  onMesureCorrectiveCancel: () => {},
  isMesureCorrectiveSending: false,
  isMesureCorrectiveError: false,
  hasMesureCorrectiveButton: false,
};

export { ReviewToolbar };
